import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

import type {
  BackgroundBloomMode,
  BackgroundGlowMode,
  BackgroundMixMode,
  BackgroundMotionMode,
  BackgroundSaturationMode,
  BackgroundStyleMode,
  SideFxDensityMode,
  SideFxMode
} from "../game/types";
import { RAVE_EFFECT_IDS, type RaveBorderLevelConfig, type RaveEffectId } from "./raveBorderConfig";
import { SIDE_FX_ALLOWED_COLORS } from "./themePalette";

const MAX_POINTS = 3400;
const MAX_LINKS = 3000;
const PHI = 1.61803398875;
const TAU = Math.PI * 2;

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function fract(value: number): number {
  return value - Math.floor(value);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function pulseAt(timeSec: number, bpm: number, beatDiv: number, sharpness: number): number {
  const beat = timeSec * Math.max(1, bpm) / 60;
  const phase = fract(beat * Math.max(0.25, beatDiv));
  return Math.pow(Math.max(0, 1 - phase), Math.max(1, sharpness));
}

function hash01(index: number, seed: number): number {
  const value = Math.sin(index * 12.9898 + seed * 78.233) * 43758.5453;
  return fract(value);
}

function parseHexColor(hexColor: string): THREE.Color {
  const color = new THREE.Color(0, 0, 0);
  const cleaned = hexColor.trim().replace("#", "");
  if (cleaned.length === 6) {
    color.set(`#${cleaned}`);
  }
  return color;
}

const FALLBACK_SIDE_FX_COLOR_0 = SIDE_FX_ALLOWED_COLORS[0] ?? "#00d4ff";
const FALLBACK_SIDE_FX_COLOR_1 = SIDE_FX_ALLOWED_COLORS[1] ?? FALLBACK_SIDE_FX_COLOR_0;

export interface SeamlessEffectPositionInput {
  pointIndex: number;
  pointCount: number;
  phase: number;
  pulse: number;
  energy: number;
  transient: number;
  sidePhase: number;
  spreadX: number;
  spreadY: number;
  depth: number;
}

export type VisualizerSceneFamily = "SPEAKER_CONE" | "WARP_TUNNEL" | "GRID_CUBE";

export interface KickPulseInput {
  bassEnergy: number;
  transient: number;
}

export interface GrooveVisualBoost {
  motionRateMult: number;
  pulseAmpMult: number;
  hueShiftRate: number;
  chromaBoost: number;
}

const CUBE_EDGE_PAIRS: ReadonlyArray<
  readonly [readonly [number, number, number], readonly [number, number, number]]
> = [
  [[-1, -1, -1], [1, -1, -1]],
  [[-1, -1, -1], [-1, 1, -1]],
  [[1, -1, -1], [1, 1, -1]],
  [[-1, 1, -1], [1, 1, -1]],
  [[-1, -1, 1], [1, -1, 1]],
  [[-1, -1, 1], [-1, 1, 1]],
  [[1, -1, 1], [1, 1, 1]],
  [[-1, 1, 1], [1, 1, 1]],
  [[-1, -1, -1], [-1, -1, 1]],
  [[1, -1, -1], [1, -1, 1]],
  [[-1, 1, -1], [-1, 1, 1]],
  [[1, 1, -1], [1, 1, 1]]
];

export function computeSeamlessLoopPhase(
  songTimeSec: number,
  bpm: number,
  beatDiv: number,
  speed: number,
  loopBeats: number,
  phaseOffset: number
): number {
  const safeSongTime = Math.max(0, songTimeSec);
  const safeBpm = Math.max(1, bpm);
  const safeBeatDiv = Math.max(0.25, beatDiv);
  const safeSpeed = Math.max(0.1, speed);
  const safeLoopBeats = Math.max(1, loopBeats);
  const beat = safeSongTime * safeBpm / 60;
  const loopCycles = (beat * safeBeatDiv * safeSpeed) / safeLoopBeats;
  return fract(loopCycles + phaseOffset);
}

export function resolveMotionPhase(
  songTimeSec: number,
  bpm: number,
  beatDiv: number,
  speed: number,
  loopBeats: number,
  phaseOffset: number,
  useBpmMotion: boolean,
  ambientDriftSpeed: number
): number {
  if (useBpmMotion) {
    return computeSeamlessLoopPhase(songTimeSec, bpm, beatDiv, speed, loopBeats, phaseOffset);
  }
  const safeSongTime = Math.max(0, songTimeSec);
  const safeDrift = Math.max(0.001, ambientDriftSpeed);
  return fract(safeSongTime * safeDrift + phaseOffset);
}

export function sampleCyclicPaletteColor(palette: string[], t: number, out: THREE.Color): void {
  const safePalette = palette.length > 0 ? palette : ["#000000", FALLBACK_SIDE_FX_COLOR_0];
  const stops = safePalette.length >= 2
    ? safePalette
    : [safePalette[0] ?? "#000000", FALLBACK_SIDE_FX_COLOR_0];
  const count = stops.length;
  const wrapped = fract(t);
  const scaled = wrapped * count;
  const idx = Math.floor(scaled) % count;
  const next = (idx + 1) % count;
  const local = fract(scaled);
  const c0 = parseHexColor(stops[idx] ?? "#000000");
  const c1 = parseHexColor(stops[next] ?? FALLBACK_SIDE_FX_COLOR_1);
  out.copy(c0).lerp(c1, local);
}

function resolveEffectVariant(effectId: RaveEffectId): number {
  const idx = RAVE_EFFECT_IDS.indexOf(effectId);
  return idx < 0 ? 0 : idx;
}

function resolveDefaultFamilyForEffect(effectId: RaveEffectId): VisualizerSceneFamily {
  const idx = resolveEffectVariant(effectId);
  if (idx <= 6) {
    return "SPEAKER_CONE";
  }
  if (idx <= 13) {
    return "WARP_TUNNEL";
  }
  return "GRID_CUBE";
}

export function resolveSequenceStyleForLevel(level: number): Exclude<BackgroundStyleMode, "SEQUENCE"> {
  const safeLevel = Math.max(1, Math.floor(level));
  const sequence = (safeLevel - 1) % 3;
  if (sequence === 1) {
    return "CYBER_GRID";
  }
  if (sequence === 2) {
    return "TUNNEL";
  }
  return "SYNTHWAVE";
}

export function resolveVisualizerSceneFamily(
  backgroundStyleMode: BackgroundStyleMode,
  level: number = 1
): VisualizerSceneFamily {
  const effectiveStyle = backgroundStyleMode === "SEQUENCE"
    ? resolveSequenceStyleForLevel(level)
    : backgroundStyleMode;
  if (effectiveStyle === "CYBER_GRID") {
    return "WARP_TUNNEL";
  }
  if (effectiveStyle === "TUNNEL") {
    return "GRID_CUBE";
  }
  return "SPEAKER_CONE";
}

export function resolveKickPulse(
  audio: KickPulseInput,
  dt: number,
  previous: number
): number {
  const safeDt = clamp(dt, 0, 0.25);
  const bass = clamp01(audio.bassEnergy);
  const transient = clamp01(audio.transient);
  const target = clamp01(bass * 0.72 + transient * 0.28);
  const attackPerSec = 14;
  const releasePerSec = 4.2;
  const blend = target >= previous
    ? clamp01(attackPerSec * safeDt)
    : clamp01(releasePerSec * safeDt);
  return clamp01(previous + (target - previous) * blend);
}

export function resolveGrooveVisualBoost(
  grooveActive: boolean,
  basePulse: number,
  songTimeSec: number,
  bpm: number
): GrooveVisualBoost {
  const safeBpm = Math.max(1, bpm);
  const beat = songTimeSec * safeBpm / 60;
  const beatPulse = 0.5 + 0.5 * Math.sin(beat * TAU);
  if (!grooveActive) {
    return {
      motionRateMult: 1,
      pulseAmpMult: 1,
      hueShiftRate: 0.2 + clamp01(basePulse) * 0.06,
      chromaBoost: 1
    };
  }
  return {
    motionRateMult: 1.32 + beatPulse * 0.08,
    pulseAmpMult: 1.28 + beatPulse * 0.12,
    hueShiftRate: 0.52 + beatPulse * 0.18,
    chromaBoost: 1.18 + beatPulse * 0.12
  };
}

function sampleSceneFamilyPosition(
  sceneFamily: VisualizerSceneFamily,
  variant: number,
  input: SeamlessEffectPositionInput,
  out: THREE.Vector3
): void {
  const count = Math.max(1, input.pointCount);
  const pointIndex = Math.max(0, input.pointIndex);
  const u = pointIndex / Math.max(1, count - 1);
  const spiral = fract(pointIndex * PHI);
  const angle = spiral * TAU;
  const seedA = hash01(pointIndex, 1.3 + variant * 0.07);
  const seedB = hash01(pointIndex, 2.7 + variant * 0.11);
  const seedC = hash01(pointIndex, 4.1 + variant * 0.13);
  const phase = fract(input.phase);
  const theta = phase * TAU;
  const variant01 = variant / Math.max(1, RAVE_EFFECT_IDS.length - 1);
  const harmonicA = 1 + (variant % 3);
  const harmonicB = 2 + (variant % 4);
  const harmonicC = 1 + ((variant + 1) % 3);
  const beatLift = 1 + input.pulse * (0.11 + variant01 * 0.08) + input.transient * 0.10 + input.energy * 0.06;

  let x = 0;
  let y = 0;
  let z = 0;

  if (sceneFamily === "SPEAKER_CONE") {
    const ringCount = 6 + (variant % 5);
    const ringBand = Math.min(ringCount - 1, Math.floor(u * ringCount));
    const ringNorm = ringBand / Math.max(1, ringCount - 1);
    const ringLocal = fract(u * ringCount);
    const spokeCount = 8 + (variant % 6);
    const spoke = Math.floor(ringLocal * spokeCount);
    const a = (spoke / spokeCount) * TAU + theta * harmonicA + seedA * 0.22;
    const coneRadius = (0.35 + ringNorm * (2.2 + variant01 * 1.1))
      * (1 + input.pulse * 0.28 + input.transient * 0.1);
    const rimWarp = Math.sin(theta * harmonicB + ringNorm * TAU * (2 + (variant % 3)) + seedB * 4) * 0.12;
    x = Math.cos(a) * (coneRadius + rimWarp);
    y = Math.sin(a) * (coneRadius + rimWarp) * (0.8 + variant01 * 0.25);
    z = -2.2 - ringNorm * (9.5 + variant01 * 4.5);
    if (u > 0.82) {
      const burst = Math.pow((u - 0.82) / 0.18, 0.72);
      const orbit = 2.0 + burst * 3.6;
      x = Math.cos(angle + theta * harmonicA) * orbit + (seedA * 2 - 1) * 0.35;
      y = Math.sin(angle + theta * harmonicC) * orbit + (seedB * 2 - 1) * 0.35;
      z = -6.5 + Math.sin(seedC * TAU + theta * harmonicB) * 5.0;
    }
  } else if (sceneFamily === "WARP_TUNNEL") {
    const tubeRadius = 1.2 + (0.5 + 0.5 * Math.cos(u * TAU * (2 + (variant % 3)) - theta * harmonicA)) * (2.1 + variant01 * 0.8);
    const spin = theta * harmonicB + u * TAU * (3 + (variant % 3));
    const warp = Math.sin(u * TAU * (3 + (variant % 2)) + theta * harmonicB + seedA * TAU) * 0.24;
    x = Math.cos(angle + spin) * (tubeRadius + warp);
    y = Math.sin(angle + spin) * (tubeRadius * (0.9 + variant01 * 0.25) + warp);
    z = -9 + Math.sin(u * TAU * (2 + (variant % 3)) + theta * harmonicC) * (12.5 + variant01 * 4.0);
    if (u > 0.9) {
      x += (seedA * 2 - 1) * 1.1;
      y += (seedB * 2 - 1) * 1.1;
    }
  } else {
    const edgeBand = 0.72;
    const cubeSize = 2.1 + variant01 * 1.1;
    if (u <= edgeBand) {
      const edgeU = u / edgeBand;
      const edgeIndex = Math.floor(edgeU * CUBE_EDGE_PAIRS.length) % CUBE_EDGE_PAIRS.length;
      const edgeLocal = fract(edgeU * CUBE_EDGE_PAIRS.length);
      const edge = CUBE_EDGE_PAIRS[edgeIndex];
      const ax = edge[0][0] * cubeSize;
      const ay = edge[0][1] * cubeSize;
      const az = edge[0][2] * cubeSize;
      const bx = edge[1][0] * cubeSize;
      const by = edge[1][1] * cubeSize;
      const bz = edge[1][2] * cubeSize;
      x = lerp(ax, bx, edgeLocal);
      y = lerp(ay, by, edgeLocal);
      z = lerp(az, bz, edgeLocal) - 8.2;
    } else {
      const particleU = (u - edgeBand) / (1 - edgeBand);
      const orbitR = 2.8 + seedA * 2.6;
      const orbitA = angle + theta * harmonicA;
      x = Math.cos(orbitA) * orbitR + (seedA * 2 - 1) * 0.28;
      y = Math.sin(angle * (1 + (variant % 2)) + theta * harmonicC) * orbitR * 0.82 + (seedB * 2 - 1) * 0.28;
      z = -8 + Math.sin(seedC * TAU + theta * harmonicB + particleU * TAU * 0.75) * 8.4;
    }

    const rotY = theta * harmonicA;
    const rotX = theta * harmonicC;
    const cY = Math.cos(rotY);
    const sY = Math.sin(rotY);
    const rx = x * cY - z * sY;
    const rz = x * sY + z * cY;
    const cX = Math.cos(rotX);
    const sX = Math.sin(rotX);
    const ry = y * cX - rz * sX;
    const rz2 = y * sX + rz * cX;
    x = rx;
    y = ry;
    z = rz2;
  }

  x *= beatLift * input.spreadX;
  y *= beatLift * input.spreadY;
  z *= input.depth;

  if (input.sidePhase > 0.5) {
    x = -x;
  }

  out.set(x, y, z);
}

export function sampleEffectPositionSeamless(
  effectId: RaveEffectId,
  input: SeamlessEffectPositionInput,
  out: THREE.Vector3
): void {
  const family = resolveDefaultFamilyForEffect(effectId);
  const variant = resolveEffectVariant(effectId);
  sampleSceneFamilyPosition(family, variant, input, out);
}

export interface ThreeSideRuntimeConfig extends RaveBorderLevelConfig {
  intensityBoost: number;
}

export interface ThreeSideAudioState {
  energy: number;
  transient: number;
  rawEnergy: number;
}

export interface ThreeSideStripBounds {
  left: { x: number; width: number };
  right: { x: number; width: number };
}

export interface ThreeSideRenderInput {
  active: boolean;
  grooveActive: boolean;
  shellWidth: number;
  shellHeight: number;
  stripBounds: ThreeSideStripBounds;
  songTimeSec: number;
  sideFxMode: SideFxMode;
  sideFxDensityMode: SideFxDensityMode;
  backgroundStyleMode: BackgroundStyleMode;
  backgroundBloomMode: BackgroundBloomMode;
  backgroundGlowMode: BackgroundGlowMode;
  backgroundSaturationMode: BackgroundSaturationMode;
  backgroundMixMode: BackgroundMixMode;
  backgroundMotionMode: BackgroundMotionMode;
  reducedMotion: boolean;
  lowPower: boolean;
  transitionMix: number;
  fromConfig: ThreeSideRuntimeConfig;
  toConfig: ThreeSideRuntimeConfig;
  audio: ThreeSideAudioState;
}

export interface BackgroundRenderTuning {
  bloomEnabled: boolean;
  bloomStrengthMult: number;
  bloomRadiusMult: number;
  bloomThresholdOffset: number;
  glowOpacityMult: number;
  chromaMult: number;
  saturationMult: number;
  lineBudgetMult: number;
  pointBudgetMult: number;
  motionSpeedMult: number;
  useBpmMotion: boolean;
  ambientDriftSpeed: number;
  styleSpreadXMult: number;
  styleSpreadYMult: number;
  styleDepthMult: number;
  styleLinkMult: number;
  styleCameraZOffset: number;
  styleFovOffset: number;
}

export interface SceneProfile {
  spreadX: number;
  spreadY: number;
  depth: number;
  cameraZ: number;
  cameraFov: number;
  pointBudgetScale: number;
  linkBudgetScale: number;
  linkStrideBase: number;
  linkStrideRange: number;
  linkDiversity: number;
  bloomStrength: number;
  bloomRadius: number;
  bloomThreshold: number;
}

export interface NeonColorWeights {
  whiteLift: number;
  chromaGain: number;
  saturation: number;
  energyLift: number;
}

interface SceneProfileSeed {
  spreadX: number;
  spreadY: number;
  depth: number;
  cameraZ: number;
  cameraFov: number;
  pointBudgetScale: number;
  linkBudgetScale: number;
  linkStrideBase: number;
  linkStrideRange: number;
  linkDiversity: number;
  bloomStrength: number;
  bloomRadius: number;
  bloomThreshold: number;
}

const SCENE_PROFILE_BASE: Record<RaveEffectId, SceneProfileSeed> = {
  grid_horizon_starfield: {
    spreadX: 1.72, spreadY: 1.68, depth: 1.08, cameraZ: 5.8, cameraFov: 49,
    pointBudgetScale: 1.18, linkBudgetScale: 1.16, linkStrideBase: 6, linkStrideRange: 18, linkDiversity: 1.15,
    bloomStrength: 1.55, bloomRadius: 0.36, bloomThreshold: 0.56
  },
  grid_wave_floor: {
    spreadX: 1.64, spreadY: 1.58, depth: 1.02, cameraZ: 5.9, cameraFov: 48,
    pointBudgetScale: 1.15, linkBudgetScale: 1.08, linkStrideBase: 6, linkStrideRange: 16, linkDiversity: 1.1,
    bloomStrength: 1.5, bloomRadius: 0.34, bloomThreshold: 0.57
  },
  grid_perspective_split: {
    spreadX: 1.60, spreadY: 1.64, depth: 1.05, cameraZ: 5.85, cameraFov: 47,
    pointBudgetScale: 1.16, linkBudgetScale: 1.1, linkStrideBase: 7, linkStrideRange: 18, linkDiversity: 1.12,
    bloomStrength: 1.58, bloomRadius: 0.36, bloomThreshold: 0.56
  },
  grid_hyperplane: {
    spreadX: 1.66, spreadY: 1.66, depth: 1.06, cameraZ: 5.75, cameraFov: 48,
    pointBudgetScale: 1.14, linkBudgetScale: 1.14, linkStrideBase: 6, linkStrideRange: 17, linkDiversity: 1.14,
    bloomStrength: 1.56, bloomRadius: 0.35, bloomThreshold: 0.56
  },
  box_tunnel_axis: {
    spreadX: 1.72, spreadY: 1.74, depth: 1.12, cameraZ: 5.65, cameraFov: 46,
    pointBudgetScale: 1.12, linkBudgetScale: 1.2, linkStrideBase: 7, linkStrideRange: 20, linkDiversity: 1.18,
    bloomStrength: 1.62, bloomRadius: 0.37, bloomThreshold: 0.54
  },
  box_tunnel_twist: {
    spreadX: 1.76, spreadY: 1.76, depth: 1.14, cameraZ: 5.6, cameraFov: 45,
    pointBudgetScale: 1.15, linkBudgetScale: 1.24, linkStrideBase: 8, linkStrideRange: 21, linkDiversity: 1.2,
    bloomStrength: 1.66, bloomRadius: 0.38, bloomThreshold: 0.54
  },
  box_tunnel_nested: {
    spreadX: 1.74, spreadY: 1.72, depth: 1.13, cameraZ: 5.55, cameraFov: 45,
    pointBudgetScale: 1.14, linkBudgetScale: 1.24, linkStrideBase: 8, linkStrideRange: 20, linkDiversity: 1.2,
    bloomStrength: 1.68, bloomRadius: 0.39, bloomThreshold: 0.53
  },
  box_tunnel_lattice_chase: {
    spreadX: 1.78, spreadY: 1.78, depth: 1.16, cameraZ: 5.5, cameraFov: 44,
    pointBudgetScale: 1.2, linkBudgetScale: 1.3, linkStrideBase: 9, linkStrideRange: 22, linkDiversity: 1.25,
    bloomStrength: 1.72, bloomRadius: 0.40, bloomThreshold: 0.52
  },
  radial_tube_spokes: {
    spreadX: 1.62, spreadY: 1.66, depth: 1.14, cameraZ: 5.8, cameraFov: 47,
    pointBudgetScale: 1.12, linkBudgetScale: 1.18, linkStrideBase: 6, linkStrideRange: 19, linkDiversity: 1.18,
    bloomStrength: 1.6, bloomRadius: 0.36, bloomThreshold: 0.55
  },
  radial_tube_spiral: {
    spreadX: 1.68, spreadY: 1.7, depth: 1.18, cameraZ: 5.7, cameraFov: 46,
    pointBudgetScale: 1.14, linkBudgetScale: 1.2, linkStrideBase: 7, linkStrideRange: 20, linkDiversity: 1.2,
    bloomStrength: 1.64, bloomRadius: 0.37, bloomThreshold: 0.54
  },
  radial_vortex_braid: {
    spreadX: 1.74, spreadY: 1.74, depth: 1.2, cameraZ: 5.6, cameraFov: 45,
    pointBudgetScale: 1.16, linkBudgetScale: 1.26, linkStrideBase: 8, linkStrideRange: 21, linkDiversity: 1.24,
    bloomStrength: 1.7, bloomRadius: 0.39, bloomThreshold: 0.52
  },
  radial_blackhole_pre: {
    spreadX: 1.82, spreadY: 1.82, depth: 1.22, cameraZ: 5.4, cameraFov: 44,
    pointBudgetScale: 1.18, linkBudgetScale: 1.28, linkStrideBase: 9, linkStrideRange: 23, linkDiversity: 1.28,
    bloomStrength: 1.78, bloomRadius: 0.42, bloomThreshold: 0.51
  },
  voxel_room_stack: {
    spreadX: 1.65, spreadY: 1.64, depth: 1.1, cameraZ: 5.85, cameraFov: 47,
    pointBudgetScale: 1.22, linkBudgetScale: 1.14, linkStrideBase: 6, linkStrideRange: 18, linkDiversity: 1.14,
    bloomStrength: 1.58, bloomRadius: 0.36, bloomThreshold: 0.55
  },
  voxel_blocks_drift: {
    spreadX: 1.7, spreadY: 1.7, depth: 1.12, cameraZ: 5.75, cameraFov: 46,
    pointBudgetScale: 1.24, linkBudgetScale: 1.18, linkStrideBase: 7, linkStrideRange: 19, linkDiversity: 1.16,
    bloomStrength: 1.62, bloomRadius: 0.37, bloomThreshold: 0.54
  },
  voxel_room_cascade: {
    spreadX: 1.74, spreadY: 1.76, depth: 1.14, cameraZ: 5.65, cameraFov: 45,
    pointBudgetScale: 1.28, linkBudgetScale: 1.2, linkStrideBase: 7, linkStrideRange: 20, linkDiversity: 1.18,
    bloomStrength: 1.68, bloomRadius: 0.39, bloomThreshold: 0.53
  },
  voxel_particle_hybrid: {
    spreadX: 1.8, spreadY: 1.82, depth: 1.16, cameraZ: 5.5, cameraFov: 44,
    pointBudgetScale: 1.34, linkBudgetScale: 1.26, linkStrideBase: 8, linkStrideRange: 22, linkDiversity: 1.24,
    bloomStrength: 1.78, bloomRadius: 0.42, bloomThreshold: 0.51
  },
  warp_mesh_ripple: {
    spreadX: 1.84, spreadY: 1.84, depth: 1.2, cameraZ: 5.45, cameraFov: 44,
    pointBudgetScale: 1.32, linkBudgetScale: 1.24, linkStrideBase: 8, linkStrideRange: 23, linkDiversity: 1.23,
    bloomStrength: 1.82, bloomRadius: 0.42, bloomThreshold: 0.50
  },
  orbit_ring_rails: {
    spreadX: 1.88, spreadY: 1.86, depth: 1.22, cameraZ: 5.35, cameraFov: 43,
    pointBudgetScale: 1.3, linkBudgetScale: 1.28, linkStrideBase: 9, linkStrideRange: 24, linkDiversity: 1.26,
    bloomStrength: 1.88, bloomRadius: 0.44, bloomThreshold: 0.49
  },
  orbit_ringfield_star: {
    spreadX: 1.92, spreadY: 1.92, depth: 1.24, cameraZ: 5.2, cameraFov: 42,
    pointBudgetScale: 1.35, linkBudgetScale: 1.3, linkStrideBase: 9, linkStrideRange: 25, linkDiversity: 1.28,
    bloomStrength: 1.92, bloomRadius: 0.45, bloomThreshold: 0.48
  },
  blackhole_warp_finale: {
    spreadX: 1.95, spreadY: 1.95, depth: 1.28, cameraZ: 5.1, cameraFov: 41,
    pointBudgetScale: 1.4, linkBudgetScale: 1.34, linkStrideBase: 10, linkStrideRange: 26, linkDiversity: 1.32,
    bloomStrength: 1.96, bloomRadius: 0.46, bloomThreshold: 0.47
  }
};

function densityScale(mode: SideFxDensityMode): number {
  if (mode === "LOW") return 0.72;
  if (mode === "HIGH") return 1.28;
  return 1;
}

export function resolveBackgroundRenderTuning(input: ThreeSideRenderInput): BackgroundRenderTuning {
  const bloomScaleByMode: Record<BackgroundBloomMode, { enabled: boolean; strength: number; radius: number; thresholdOffset: number }> = {
    OFF: { enabled: false, strength: 0, radius: 0, thresholdOffset: 0.22 },
    LOW: { enabled: true, strength: 0.18, radius: 0.52, thresholdOffset: 0.24 },
    MEDIUM: { enabled: true, strength: 0.45, radius: 0.8, thresholdOffset: 0.1 },
    HIGH: { enabled: true, strength: 0.8, radius: 1.0, thresholdOffset: 0.02 }
  };
  const glowScaleByMode: Record<BackgroundGlowMode, number> = {
    LOW: 0.78,
    MEDIUM: 1,
    HIGH: 1.22
  };
  const saturationScaleByMode: Record<BackgroundSaturationMode, number> = {
    LOW: 0.86,
    MEDIUM: 1,
    HIGH: 1.18,
    MAX: 1.34
  };
  const mixScaleByMode: Record<BackgroundMixMode, { line: number; point: number }> = {
    LINES: { line: 1.28, point: 0.74 },
    EVEN: { line: 1, point: 1 },
    PARTICLES: { line: 0.78, point: 1.3 }
  };
  const motionScaleByMode: Record<BackgroundMotionMode, { bpm: boolean; speed: number; drift: number }> = {
    OFF: { bpm: false, speed: 1, drift: 0.04 },
    CALM: { bpm: true, speed: 0.82, drift: 0.03 },
    NORMAL: { bpm: true, speed: 1, drift: 0.04 },
    FAST: { bpm: true, speed: 1.2, drift: 0.05 }
  };
  const styleBiasByMode: Record<
    Exclude<BackgroundStyleMode, "SEQUENCE">,
    { spreadX: number; spreadY: number; depth: number; link: number; cameraZOffset: number; fovOffset: number }
  > = {
    SYNTHWAVE: { spreadX: 1.04, spreadY: 1.06, depth: 1.08, link: 1.02, cameraZOffset: -0.18, fovOffset: 1 },
    CYBER_GRID: { spreadX: 1.18, spreadY: 1.2, depth: 1.22, link: 1.16, cameraZOffset: -0.42, fovOffset: -2 },
    TUNNEL: { spreadX: 1.12, spreadY: 1.14, depth: 1.12, link: 1.1, cameraZOffset: -0.3, fovOffset: 0 }
  };

  const bloomProfile = bloomScaleByMode[input.backgroundBloomMode];
  const mixProfile = mixScaleByMode[input.backgroundMixMode];
  const effectiveStyleMode = input.backgroundStyleMode === "SEQUENCE"
    ? resolveSequenceStyleForLevel(input.toConfig.level)
    : input.backgroundStyleMode;
  const styleProfile = styleBiasByMode[effectiveStyleMode];

  let bloomEnabled =
    bloomProfile.enabled
    && input.sideFxMode === "FULL"
    && !input.lowPower
    && !input.reducedMotion;
  let bloomStrengthMult = bloomProfile.strength;
  let bloomRadiusMult = bloomProfile.radius;
  let bloomThresholdOffset = bloomProfile.thresholdOffset;

  let glowOpacityMult = glowScaleByMode[input.backgroundGlowMode];
  let chromaMult = saturationScaleByMode[input.backgroundSaturationMode];
  let saturationMult = saturationScaleByMode[input.backgroundSaturationMode];

  let lineBudgetMult = mixProfile.line;
  let pointBudgetMult = mixProfile.point;
  let useBpmMotion = motionScaleByMode[input.backgroundMotionMode].bpm;
  let motionSpeedMult = motionScaleByMode[input.backgroundMotionMode].speed;
  let ambientDriftSpeed = motionScaleByMode[input.backgroundMotionMode].drift;

  let styleSpreadXMult = styleProfile.spreadX;
  let styleSpreadYMult = styleProfile.spreadY;
  let styleDepthMult = styleProfile.depth;
  let styleLinkMult = styleProfile.link;
  let styleCameraZOffset = styleProfile.cameraZOffset;
  let styleFovOffset = styleProfile.fovOffset;

  if (input.sideFxMode === "SUBTLE") {
    bloomEnabled = false;
    bloomStrengthMult *= 0.68;
    bloomRadiusMult *= 0.85;
    bloomThresholdOffset += 0.05;
    glowOpacityMult *= 0.82;
    chromaMult *= 0.88;
    saturationMult *= 0.9;
    lineBudgetMult *= 0.9;
    pointBudgetMult *= 0.9;
  }

  if (input.reducedMotion) {
    bloomEnabled = false;
    glowOpacityMult *= 0.82;
    chromaMult *= 0.9;
    saturationMult *= 0.88;
    lineBudgetMult *= 0.85;
    pointBudgetMult *= 0.85;
    motionSpeedMult = Math.min(motionSpeedMult, 0.9);
    ambientDriftSpeed *= 0.8;
  }

  if (input.lowPower) {
    bloomEnabled = false;
    glowOpacityMult *= 0.92;
    lineBudgetMult *= 0.86;
    pointBudgetMult *= 0.84;
    motionSpeedMult *= 0.92;
    ambientDriftSpeed *= 0.9;
    styleSpreadXMult *= 0.98;
    styleSpreadYMult *= 0.98;
  }

  return {
    bloomEnabled,
    bloomStrengthMult: clamp(bloomStrengthMult, 0, 1.45),
    bloomRadiusMult: clamp(bloomRadiusMult, 0.45, 1.3),
    bloomThresholdOffset: clamp(bloomThresholdOffset, -0.1, 0.3),
    glowOpacityMult: clamp(glowOpacityMult, 0.58, 1.35),
    chromaMult: clamp(chromaMult, 0.82, 1.45),
    saturationMult: clamp(saturationMult, 0.78, 1.45),
    lineBudgetMult: clamp(lineBudgetMult, 0.62, 1.42),
    pointBudgetMult: clamp(pointBudgetMult, 0.62, 1.42),
    motionSpeedMult: clamp(motionSpeedMult, 0.7, 1.28),
    useBpmMotion,
    ambientDriftSpeed: clamp(ambientDriftSpeed, 0.01, 0.2),
    styleSpreadXMult: clamp(styleSpreadXMult, 0.88, 1.3),
    styleSpreadYMult: clamp(styleSpreadYMult, 0.88, 1.3),
    styleDepthMult: clamp(styleDepthMult, 0.88, 1.32),
    styleLinkMult: clamp(styleLinkMult, 0.88, 1.28),
    styleCameraZOffset: clamp(styleCameraZOffset, -0.7, 0.25),
    styleFovOffset: clamp(styleFovOffset, -6, 5)
  };
}

export function resolveSceneProfile(
  effectId: RaveEffectId,
  sideFxMode: SideFxMode,
  sideFxDensityMode: SideFxDensityMode
): SceneProfile {
  const seed = SCENE_PROFILE_BASE[effectId];
  const density = densityScale(sideFxDensityMode);
  const subtle = sideFxMode === "SUBTLE";
  const subtleSpread = subtle ? 0.96 : 1;
  const subtleBloom = subtle ? 0.66 : 1;

  return {
    spreadX: seed.spreadX * subtleSpread,
    spreadY: seed.spreadY * subtleSpread,
    depth: seed.depth,
    cameraZ: seed.cameraZ,
    cameraFov: seed.cameraFov,
    pointBudgetScale: seed.pointBudgetScale * density * (subtle ? 0.9 : 1),
    linkBudgetScale: seed.linkBudgetScale * density * (subtle ? 0.88 : 1),
    linkStrideBase: seed.linkStrideBase,
    linkStrideRange: seed.linkStrideRange,
    linkDiversity: seed.linkDiversity * (subtle ? 0.92 : 1),
    bloomStrength: seed.bloomStrength * subtleBloom,
    bloomRadius: seed.bloomRadius * (subtle ? 0.84 : 1),
    bloomThreshold: clamp(seed.bloomThreshold + (subtle ? 0.06 : 0), 0.42, 0.78)
  };
}

export function computeNeonColorWeights(level: number, pulse: number, energy: number): NeonColorWeights {
  const safeLevel = clamp(level, 1, 20);
  const levelBoost = (safeLevel - 1) / 19;
  return {
    whiteLift: clamp(0.04 + pulse * 0.04 + energy * 0.03, 0.03, 0.12),
    chromaGain: clamp(1.25 + levelBoost * 0.45 + pulse * 0.2 + energy * 0.2, 1.2, 2.1),
    saturation: clamp(1.28 + levelBoost * 0.22 + energy * 0.12, 1.2, 1.85),
    energyLift: clamp(0.7 + energy * 0.42 + pulse * 0.16, 0.7, 1.4)
  };
}

interface SizeState {
  width: number;
  height: number;
  dpr: number;
}

export class ThreeSideFxRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly pointsGeom: THREE.BufferGeometry;
  private readonly linksGeom: THREE.BufferGeometry;
  private readonly pointsMaterial: THREE.PointsMaterial;
  private readonly linksMaterial: THREE.LineBasicMaterial;
  private readonly points: THREE.Points;
  private readonly links: THREE.LineSegments;

  private readonly pointPositions = new Float32Array(MAX_POINTS * 3);
  private readonly pointColors = new Float32Array(MAX_POINTS * 3);
  private readonly linkPositions = new Float32Array(MAX_LINKS * 2 * 3);
  private readonly linkColors = new Float32Array(MAX_LINKS * 2 * 3);

  private readonly tmpA = new THREE.Vector3();
  private readonly tmpB = new THREE.Vector3();
  private readonly tmpColorA = new THREE.Color();
  private readonly tmpColorB = new THREE.Color();
  private readonly tmpColorOut = new THREE.Color();

  private renderer: THREE.WebGLRenderer | null = null;
  private composer: EffectComposer | null = null;
  private renderPass: RenderPass | null = null;
  private bloomPass: UnrealBloomPass | null = null;
  private ready = false;
  private lastPaintSec = -Infinity;
  private lastKickUpdateSec = Number.NaN;
  private kickPulse = 0;
  private sizeState: SizeState = { width: 1, height: 1, dpr: 1 };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(58, 1, 0.1, 120);
    this.camera.position.set(0, 0, 8);

    this.pointsGeom = new THREE.BufferGeometry();
    this.pointsGeom.setAttribute("position", new THREE.BufferAttribute(this.pointPositions, 3));
    this.pointsGeom.setAttribute("color", new THREE.BufferAttribute(this.pointColors, 3));

    this.linksGeom = new THREE.BufferGeometry();
    this.linksGeom.setAttribute("position", new THREE.BufferAttribute(this.linkPositions, 3));
    this.linksGeom.setAttribute("color", new THREE.BufferAttribute(this.linkColors, 3));

    this.pointsMaterial = new THREE.PointsMaterial({
      size: 0.07,
      sizeAttenuation: true,
      transparent: true,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.92
    });
    this.linksMaterial = new THREE.LineBasicMaterial({
      transparent: true,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.88
    });

    this.points = new THREE.Points(this.pointsGeom, this.pointsMaterial);
    this.links = new THREE.LineSegments(this.linksGeom, this.linksMaterial);
    this.scene.add(this.links);
    this.scene.add(this.points);

    this.initRenderer();
  }

  isReady(): boolean {
    return this.ready;
  }

  private initRenderer(): void {
    try {
      const renderer = new THREE.WebGLRenderer({
        canvas: this.canvas,
        alpha: true,
        antialias: false,
        depth: true,
        stencil: false,
        powerPreference: "high-performance",
        premultipliedAlpha: true
      });
      renderer.autoClear = false;
      renderer.outputColorSpace = THREE.SRGBColorSpace;

      const renderPass = new RenderPass(this.scene, this.camera);
      const bloomPass = new UnrealBloomPass(new THREE.Vector2(1, 1), 1.05, 0.35, 0.72);
      const composer = new EffectComposer(renderer);
      composer.addPass(renderPass);
      composer.addPass(bloomPass);

      this.renderer = renderer;
      this.renderPass = renderPass;
      this.bloomPass = bloomPass;
      this.composer = composer;
      this.ready = true;
    } catch {
      this.ready = false;
      this.renderer = null;
      this.renderPass = null;
      this.bloomPass = null;
      this.composer = null;
    }
  }

  private lerpSceneProfile(a: SceneProfile, b: SceneProfile, t: number): SceneProfile {
    return {
      spreadX: lerp(a.spreadX, b.spreadX, t),
      spreadY: lerp(a.spreadY, b.spreadY, t),
      depth: lerp(a.depth, b.depth, t),
      cameraZ: lerp(a.cameraZ, b.cameraZ, t),
      cameraFov: lerp(a.cameraFov, b.cameraFov, t),
      pointBudgetScale: lerp(a.pointBudgetScale, b.pointBudgetScale, t),
      linkBudgetScale: lerp(a.linkBudgetScale, b.linkBudgetScale, t),
      linkStrideBase: lerp(a.linkStrideBase, b.linkStrideBase, t),
      linkStrideRange: lerp(a.linkStrideRange, b.linkStrideRange, t),
      linkDiversity: lerp(a.linkDiversity, b.linkDiversity, t),
      bloomStrength: lerp(a.bloomStrength, b.bloomStrength, t),
      bloomRadius: lerp(a.bloomRadius, b.bloomRadius, t),
      bloomThreshold: lerp(a.bloomThreshold, b.bloomThreshold, t)
    };
  }

  private applyRenderTuningToProfile(profile: SceneProfile, tuning: BackgroundRenderTuning): SceneProfile {
    return {
      spreadX: profile.spreadX * tuning.styleSpreadXMult,
      spreadY: profile.spreadY * tuning.styleSpreadYMult,
      depth: profile.depth * tuning.styleDepthMult,
      cameraZ: clamp(profile.cameraZ + tuning.styleCameraZOffset, 4.2, 7.0),
      cameraFov: clamp(profile.cameraFov + tuning.styleFovOffset, 36, 62),
      pointBudgetScale: profile.pointBudgetScale,
      linkBudgetScale: profile.linkBudgetScale * tuning.styleLinkMult,
      linkStrideBase: profile.linkStrideBase,
      linkStrideRange: profile.linkStrideRange,
      linkDiversity: profile.linkDiversity * tuning.styleLinkMult,
      bloomStrength: profile.bloomStrength * tuning.bloomStrengthMult,
      bloomRadius: profile.bloomRadius * tuning.bloomRadiusMult,
      bloomThreshold: clamp(profile.bloomThreshold + tuning.bloomThresholdOffset, 0.42, 0.92)
    };
  }

  private resolveDpr(lowPower: boolean): number {
    const dpr = typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
    return Math.min(dpr, lowPower ? 1.2 : 2);
  }

  private syncSize(width: number, height: number, dpr: number): void {
    if (this.renderer == null) return;
    const pixelWidth = Math.max(1, Math.round(width * dpr));
    const pixelHeight = Math.max(1, Math.round(height * dpr));
    if (
      this.sizeState.width !== pixelWidth
      || this.sizeState.height !== pixelHeight
      || Math.abs(this.sizeState.dpr - dpr) > 1e-6
    ) {
      this.sizeState = { width: pixelWidth, height: pixelHeight, dpr };
      this.renderer.setPixelRatio(1);
      this.renderer.setSize(pixelWidth, pixelHeight, false);
      this.composer?.setSize(pixelWidth, pixelHeight);
      this.bloomPass?.setSize(pixelWidth, pixelHeight);
    }
  }

  private clearFullTransparent(): void {
    if (this.renderer == null) return;
    this.renderer.setScissorTest(false);
    this.renderer.setViewport(0, 0, this.sizeState.width, this.sizeState.height);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.clear(true, true, true);
  }

  private clearSideBlack(x: number, width: number): void {
    if (this.renderer == null) return;
    const px = Math.round(x * this.sizeState.dpr);
    const pw = Math.max(1, Math.round(width * this.sizeState.dpr));
    this.renderer.setScissorTest(true);
    this.renderer.setViewport(px, 0, pw, this.sizeState.height);
    this.renderer.setScissor(px, 0, pw, this.sizeState.height);
    this.renderer.setClearColor(0x000000, 1);
    this.renderer.clear(true, false, false);
  }

  private renderSide(
    sideIndex: 0 | 1,
    sideX: number,
    sideWidth: number,
    input: ThreeSideRenderInput,
    timeSec: number,
    kickPulse: number,
    grooveBoost: GrooveVisualBoost
  ): void {
    if (this.renderer == null || sideWidth <= 0) return;

    this.clearSideBlack(sideX, sideWidth);

    const tuning = resolveBackgroundRenderTuning(input);

    const fromProfile = this.applyRenderTuningToProfile(
      resolveSceneProfile(input.fromConfig.effectId, input.sideFxMode, input.sideFxDensityMode),
      tuning
    );
    const toProfile = this.applyRenderTuningToProfile(
      resolveSceneProfile(input.toConfig.effectId, input.sideFxMode, input.sideFxDensityMode),
      tuning
    );
    const profile = this.lerpSceneProfile(fromProfile, toProfile, clamp01(input.transitionMix));

    const aspect = Math.max(0.22, sideWidth / Math.max(1, input.shellHeight));
    this.camera.aspect = aspect;
    this.camera.fov = profile.cameraFov;
    this.camera.position.z = profile.cameraZ;
    this.camera.position.x = 0;
    this.camera.position.y = 0;
    this.camera.lookAt(0, 0, -2);
    this.camera.updateProjectionMatrix();

    this.updateGeometry(
      sideIndex,
      input,
      timeSec,
      profile,
      fromProfile,
      toProfile,
      tuning,
      kickPulse,
      grooveBoost
    );

    const useBloom = tuning.bloomEnabled;

    if (this.bloomPass != null) {
      this.bloomPass.strength = profile.bloomStrength;
      this.bloomPass.radius = profile.bloomRadius;
      this.bloomPass.threshold = profile.bloomThreshold;
    }

    if (useBloom && this.composer != null && this.renderPass != null) {
      this.renderPass.scene = this.scene;
      this.renderPass.camera = this.camera;
      this.composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  }

  private samplePaletteColor(palette: string[], t: number, out: THREE.Color): void {
    sampleCyclicPaletteColor(palette, t, out);
  }

  private updateGeometry(
    sideIndex: 0 | 1,
    input: ThreeSideRenderInput,
    timeSec: number,
    profile: SceneProfile,
    fromProfile: SceneProfile,
    toProfile: SceneProfile,
    tuning: BackgroundRenderTuning,
    kickPulse: number,
    grooveBoost: GrooveVisualBoost
  ): void {
    const mix = clamp01(input.transitionMix);
    const fromEffectId = input.fromConfig.effectId;
    const toEffectId = input.toConfig.effectId;

    const modeMult = input.sideFxMode === "SUBTLE" ? 0.86 : 1;
    const perfMult = (input.reducedMotion ? 0.72 : 1) * (input.lowPower ? 0.8 : 1);

    const pointScale = lerp(fromProfile.pointBudgetScale, toProfile.pointBudgetScale, mix);
    const linkScale = lerp(fromProfile.linkBudgetScale, toProfile.linkBudgetScale, mix);
    const pointCount = Math.max(
      240,
      Math.min(
        MAX_POINTS,
        Math.floor(1180 * pointScale * modeMult * perfMult * tuning.pointBudgetMult)
      )
    );
    const linkCount = Math.max(
      240,
      Math.min(
        MAX_LINKS,
        Math.floor(1040 * linkScale * modeMult * perfMult * tuning.lineBudgetMult)
      )
    );

    const energyMult = lerp(input.fromConfig.audioEnergyMult, input.toConfig.audioEnergyMult, mix);
    const transientMult = lerp(input.fromConfig.transientMult, input.toConfig.transientMult, mix);
    const energy = clamp01(input.audio.energy * energyMult);
    const transient = input.reducedMotion ? 0 : clamp01(input.audio.transient * transientMult);

    const fromFamily = resolveVisualizerSceneFamily(input.backgroundStyleMode, input.fromConfig.level);
    const toFamily = resolveVisualizerSceneFamily(input.backgroundStyleMode, input.toConfig.level);
    const fromVariant = resolveEffectVariant(fromEffectId);
    const toVariant = resolveEffectVariant(toEffectId);
    const sidePhaseOffset = sideIndex === 1 ? 0.11 : 0;
    const motionRateMult = tuning.useBpmMotion ? grooveBoost.motionRateMult : 1;
    const fromPhase = resolveMotionPhase(
      timeSec,
      input.fromConfig.bpm,
      input.fromConfig.beatDiv,
      input.fromConfig.speed * tuning.motionSpeedMult * motionRateMult,
      input.fromConfig.loopBeats ?? 8,
      sidePhaseOffset,
      tuning.useBpmMotion,
      tuning.ambientDriftSpeed
    );
    const toPhase = resolveMotionPhase(
      timeSec,
      input.toConfig.bpm,
      input.toConfig.beatDiv,
      input.toConfig.speed * tuning.motionSpeedMult * motionRateMult,
      input.toConfig.loopBeats ?? 8,
      sidePhaseOffset,
      tuning.useBpmMotion,
      tuning.ambientDriftSpeed
    );
    const pulseFrom = tuning.useBpmMotion
      ? pulseAt(timeSec, input.fromConfig.bpm, input.fromConfig.beatDiv, input.fromConfig.pulseSharpness)
      : 0.4 + 0.1 * Math.sin((fromPhase + sidePhaseOffset) * TAU);
    const pulseTo = tuning.useBpmMotion
      ? pulseAt(timeSec, input.toConfig.bpm, input.toConfig.beatDiv, input.toConfig.pulseSharpness)
      : 0.4 + 0.1 * Math.sin((toPhase + sidePhaseOffset) * TAU);
    const basePulse = lerp(pulseFrom, pulseTo, mix);
    let pulse = clamp01(basePulse * 0.45 + kickPulse * 0.55 + transient * 0.15);
    if (input.grooveActive) {
      pulse = clamp01(pulse * grooveBoost.pulseAmpMult + 0.06);
    }
    const fromColorPhase = resolveMotionPhase(
      timeSec,
      input.fromConfig.bpm,
      input.fromConfig.beatDiv,
      input.fromConfig.speed * (tuning.useBpmMotion ? 0.52 : 0.2) * (1 + grooveBoost.hueShiftRate * 0.3),
      input.fromConfig.loopBeats ?? 8,
      sidePhaseOffset + 0.17,
      tuning.useBpmMotion,
      tuning.ambientDriftSpeed * 1.2
    );
    const toColorPhase = resolveMotionPhase(
      timeSec,
      input.toConfig.bpm,
      input.toConfig.beatDiv,
      input.toConfig.speed * (tuning.useBpmMotion ? 0.52 : 0.2) * (1 + grooveBoost.hueShiftRate * 0.3),
      input.toConfig.loopBeats ?? 8,
      sidePhaseOffset + 0.17,
      tuning.useBpmMotion,
      tuning.ambientDriftSpeed * 1.2
    );
    const colorPhase = lerp(fromColorPhase, toColorPhase, mix);
    const colorDrift = timeSec * (input.grooveActive ? grooveBoost.hueShiftRate : 0.16);
    const sidePhase = sideIndex === 1 ? 1 : 0;

    const levelValue = lerp(input.fromConfig.level, input.toConfig.level, mix);
    const neonWeights = computeNeonColorWeights(levelValue, pulse, energy);
    const whiteLift = clamp(neonWeights.whiteLift * 0.2, 0.005, 0.045);
    const chromaGain = neonWeights.chromaGain * neonWeights.energyLift * tuning.chromaMult * grooveBoost.chromaBoost;
    const saturation = clamp(neonWeights.saturation * tuning.saturationMult, 1.05, 2.4);

    for (let i = 0; i < pointCount; i += 1) {
      sampleSceneFamilyPosition(fromFamily, fromVariant, {
        pointIndex: i,
        pointCount,
        phase: fromPhase,
        pulse,
        energy,
        transient,
        sidePhase,
        spreadX: profile.spreadX,
        spreadY: profile.spreadY,
        depth: profile.depth
      }, this.tmpA);
      sampleSceneFamilyPosition(toFamily, toVariant, {
        pointIndex: i,
        pointCount,
        phase: toPhase,
        pulse,
        energy,
        transient,
        sidePhase,
        spreadX: profile.spreadX,
        spreadY: profile.spreadY,
        depth: profile.depth
      }, this.tmpB);

      this.tmpA.lerp(this.tmpB, mix);

      const pBase = i * 3;
      this.pointPositions[pBase + 0] = this.tmpA.x;
      this.pointPositions[pBase + 1] = this.tmpA.y;
      this.pointPositions[pBase + 2] = this.tmpA.z;

      this.samplePaletteColor(input.fromConfig.palette, i / pointCount + fromColorPhase + colorPhase + colorDrift, this.tmpColorA);
      this.samplePaletteColor(input.toConfig.palette, i / pointCount + toColorPhase + colorPhase + colorDrift, this.tmpColorB);
      this.tmpColorOut.copy(this.tmpColorA).lerp(this.tmpColorB, mix);

      this.tmpColorOut.offsetHSL(0, saturation - 1, 0);
      this.pointColors[pBase + 0] = clamp01(whiteLift + this.tmpColorOut.r * chromaGain);
      this.pointColors[pBase + 1] = clamp01(whiteLift + this.tmpColorOut.g * chromaGain);
      this.pointColors[pBase + 2] = clamp01(whiteLift + this.tmpColorOut.b * chromaGain);
    }

    const fromIdx = Math.max(0, RAVE_EFFECT_IDS.indexOf(fromEffectId));
    const toIdx = Math.max(0, RAVE_EFFECT_IDS.indexOf(toEffectId));
    const strideFrom = fromProfile.linkStrideBase + (fromIdx * 7) % Math.max(2, Math.round(fromProfile.linkStrideRange));
    const strideTo = toProfile.linkStrideBase + (toIdx * 7) % Math.max(2, Math.round(toProfile.linkStrideRange));
    const linkOffset = Math.max(2, Math.round(lerp(strideFrom, strideTo, mix)));
    const linkDiversity = Math.max(1, Math.round(lerp(fromProfile.linkDiversity, toProfile.linkDiversity, mix) * 7));

    for (let i = 0; i < linkCount; i += 1) {
      const a = (i * (2 + (i % linkDiversity))) % pointCount;
      const b = (a + linkOffset + (i % (3 + linkDiversity))) % pointCount;

      const lineBase = i * 6;
      const aBase = a * 3;
      const bBase = b * 3;
      this.linkPositions[lineBase + 0] = this.pointPositions[aBase + 0];
      this.linkPositions[lineBase + 1] = this.pointPositions[aBase + 1];
      this.linkPositions[lineBase + 2] = this.pointPositions[aBase + 2];
      this.linkPositions[lineBase + 3] = this.pointPositions[bBase + 0];
      this.linkPositions[lineBase + 4] = this.pointPositions[bBase + 1];
      this.linkPositions[lineBase + 5] = this.pointPositions[bBase + 2];

      const t = i / Math.max(1, linkCount - 1);
      this.samplePaletteColor(input.fromConfig.palette, t + fromColorPhase + colorPhase * 2 + colorDrift, this.tmpColorA);
      this.samplePaletteColor(input.toConfig.palette, t + toColorPhase + colorPhase * 2 + colorDrift, this.tmpColorB);
      this.tmpColorOut.copy(this.tmpColorA).lerp(this.tmpColorB, mix);
      this.tmpColorOut.offsetHSL(0, saturation - 1, 0);
      const linkLift = clamp(whiteLift * 0.3 + transient * 0.03, 0.006, 0.09);
      const cBase = i * 6;
      const cr = clamp01(linkLift + this.tmpColorOut.r * chromaGain * 0.88);
      const cg = clamp01(linkLift + this.tmpColorOut.g * chromaGain * 0.88);
      const cb = clamp01(linkLift + this.tmpColorOut.b * chromaGain * 0.88);
      this.linkColors[cBase + 0] = cr;
      this.linkColors[cBase + 1] = cg;
      this.linkColors[cBase + 2] = cb;
      this.linkColors[cBase + 3] = cr;
      this.linkColors[cBase + 4] = cg;
      this.linkColors[cBase + 5] = cb;
    }

    this.pointsGeom.setDrawRange(0, pointCount);
    this.linksGeom.setDrawRange(0, linkCount * 2);

    const pointsPos = this.pointsGeom.getAttribute("position") as THREE.BufferAttribute;
    const pointsCol = this.pointsGeom.getAttribute("color") as THREE.BufferAttribute;
    const linksPos = this.linksGeom.getAttribute("position") as THREE.BufferAttribute;
    const linksCol = this.linksGeom.getAttribute("color") as THREE.BufferAttribute;
    pointsPos.needsUpdate = true;
    pointsCol.needsUpdate = true;
    linksPos.needsUpdate = true;
    linksCol.needsUpdate = true;

    const sizeBase = input.sideFxMode === "SUBTLE" ? 0.082 : 0.104;
    this.pointsMaterial.size = sizeBase + pulse * (0.028 * tuning.glowOpacityMult) + kickPulse * 0.02 + energy * 0.018;

    const intensityBoost = lerp(input.fromConfig.intensityBoost, input.toConfig.intensityBoost, mix);
    const opacityScale = clamp(0.7 + intensityBoost * 0.17 + (input.grooveActive ? 0.08 : 0), 0.62, 1.12);
    this.pointsMaterial.opacity = (input.sideFxMode === "SUBTLE" ? 0.72 : 0.92) * opacityScale;
    this.linksMaterial.opacity = (input.sideFxMode === "SUBTLE" ? 0.58 : 0.86) * opacityScale;
    this.pointsMaterial.opacity *= tuning.glowOpacityMult;
    this.linksMaterial.opacity *= clamp(0.64 + tuning.glowOpacityMult * 0.36, 0.56, 1.2);
  }

  render(input: ThreeSideRenderInput): boolean {
    if (!this.ready || this.renderer == null) {
      return false;
    }

    this.canvas.style.display = input.active ? "block" : "none";

    const dpr = this.resolveDpr(input.lowPower);
    this.syncSize(input.shellWidth, input.shellHeight, dpr);

    const nowSec = performance.now() / 1000;
    const minFrameInterval = input.lowPower || input.reducedMotion ? 1 / 30 : 0;
    if (
      minFrameInterval > 0
      && Number.isFinite(this.lastPaintSec)
      && nowSec - this.lastPaintSec < minFrameInterval
    ) {
      return true;
    }
    this.lastPaintSec = nowSec;

    const kickDt = Number.isFinite(this.lastKickUpdateSec)
      ? clamp(nowSec - this.lastKickUpdateSec, 0, 0.25)
      : 1 / 60;
    this.lastKickUpdateSec = nowSec;
    this.kickPulse = resolveKickPulse(
      { bassEnergy: input.audio.energy, transient: input.audio.transient },
      kickDt,
      this.kickPulse
    );

    const blendedBpm = lerp(input.fromConfig.bpm, input.toConfig.bpm, clamp01(input.transitionMix));
    const grooveBoost = resolveGrooveVisualBoost(
      input.grooveActive,
      this.kickPulse,
      input.songTimeSec,
      blendedBpm
    );

    this.clearFullTransparent();

    if (!input.active || input.sideFxMode === "OFF") {
      return true;
    }

    this.renderSide(0, input.stripBounds.left.x, input.stripBounds.left.width, input, input.songTimeSec, this.kickPulse, grooveBoost);
    this.renderSide(1, input.stripBounds.right.x, input.stripBounds.right.width, input, input.songTimeSec, this.kickPulse, grooveBoost);

    this.renderer.setScissorTest(false);
    return true;
  }
}
